<template>
    <v-dialog v-model="visible" class="w-33">
        <v-card>
            <v-card-title class="pa-4">{{ $t('dialog.manualExecution') }}</v-card-title>

            <v-divider />

            <v-card-text>
                <v-select v-model="selectedCustomer" :label="$t('field.customer')" variant="outlined" :items="manualExecutionCustomers" item-title="name" item-value="reference" />

                <v-select v-model="selectedGroup" :label="$t('field.group')" variant="outlined" :items="groupItems" item-title="text" item-value="value" :disabled="selectedCustomer == ''" />

                <v-select v-if="actionModesVisible" v-model="selectedActionMode" :label="$t('field.mode')" variant="outlined" :items="actionModeItems" item-title="text" item-value="value" :disabled="actionModesDisabled" />

                <v-select v-model="selectedAction" :label="$t('field.action')" variant="outlined" :items="actions" item-title="name" item-value="value" :disabled="actionDisabled" />

                <v-select v-if="needApplication" v-model="selectedApplication" :label="$t('field.application')" variant="outlined" :items="applications" item-title="reference" item-value="id" />

                <v-select v-if="needApplicationDomain" v-model="selectedApplicationDomain" :label="$t('field.applicationDomain')" variant="outlined" :items="applicationDomains" item-title="applicationDomainName" item-value="applicationDomainId" />

                <v-select v-if="needProvider" v-model="selectedProvider" :label="$t('field.provider')" variant="outlined" :items="providers" item-title="name" item-value="id" />

                <v-select v-if="showSpecializationJob" v-model="selectedSpecializationJob" :label="$t('field.job')" variant="outlined" :items="specializationJobs" :item-title="jobTitle" item-value="name" />
            </v-card-text>

            <v-divider />

            <v-card-actions class="justify-end">
                <v-btn variant="text" color="error" @click="visible = false">{{ $t('action.cancel') }}</v-btn>
                <v-btn color="primary" variant="text" :disabled="!isValid" @click="launch">{{ $t('action.launch') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { computed, inject, ref, watch } from 'vue';
    import manualExecutionJson from '@/components/json/batches/manualExecutions.json';
    import { toast } from 'vue3-toastify';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const logStore = useLogStore();
    const userStore = useUserStore();

    const props = defineProps({
        customers: {
            type: Array,
            default: () => [],
        },
    });
    const emit = defineEmits(['execution-launched']);

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const visible = ref(false);

    const manualExecutions = manualExecutionJson;

    const selectedCustomer = ref('');
    const selectedGroup = ref('');
    const selectedActionMode = ref('');
    const selectedAction = ref('');
    const selectedApplication = ref('');
    const selectedApplicationDomain = ref('');
    const selectedProvider = ref('');
    const selectedSpecializationJob = ref('');

    const actionModes = ref({});
    const actions = ref([]);

    const actionModesDisabled = ref(true);
    const actionDisabled = ref(true);

    const needApplication = ref(false);
    const needApplicationDomain = ref(false);
    const needProvider = ref(false);

    const applications = ref([]);
    const applicationDomains = ref([]);
    const providers = ref([]);
    const specializationJobs = ref([]);

    const manualExecutionCustomers = computed(() => props.customers.filter(c => c.reference != 'all'));

    const groupItems = computed(() => Object.keys(manualExecutions).map(key => ({ text: key.toUpperCase(), value: key })));

    const actionModesVisible = computed(() => Object.keys(actionModes.value).length > 0);

    const actionModeItems = computed(() => Object.keys(actionModes.value).map(key => ({ text: key.toUpperCase(), value: key })));

    const showSpecializationJob = computed(() => selectedGroup.value == 'specialization' && specializationJobs.value.length > 0);

    const isValid = computed(() => {
        let needApplicationOk = !needApplication.value || (needApplication.value && selectedApplication.value != '');
        let needApplicationDomainOk = !needApplicationDomain.value || (needApplicationDomain.value && selectedApplicationDomain.value != '');
        let needProviderOk = !needProvider.value || (needProvider.value && selectedProvider.value != '');
        let needSpecializationOk = selectedGroup.value != 'specialization' || (selectedGroup.value == 'specialization' && selectedSpecializationJob.value != '');

        return selectedCustomer.value != '' && selectedGroup.value != '' && selectedAction.value != '' && needApplicationOk && needApplicationDomainOk && needProviderOk && needSpecializationOk;
    });

    const allApplicationDomains = {
        process: 0,
        processName: 'None',
        applicationDomainId: -1,
        applicationDomainName: t('field.all_application_domain'),
        type: 0,
        customerCode: '',
        modules: {}
    };

    watch(selectedCustomer, (value) => {
        if (value != '') {
            getApplications();
            getApplicationDomains();
        }
    });

    watch(selectedGroup, (value) => {
        selectedActionMode.value = '';
        selectedAction.value = '';
        actionModes.value = {};
        actions.value = [];

        needApplication.value = false;
        needApplicationDomain.value = false;
        needProvider.value = false;
        selectedApplication.value = '';
        selectedApplicationDomain.value = '';
        selectedProvider.value = '';
        selectedSpecializationJob.value = '';
        specializationJobs.value = [];

        if (value != '') {
            let groupData = manualExecutions[value];

            // MODE (IN, OUT, OTHER)
            if (groupData[Object.keys(groupData)[0]].length > 0 && !Array.isArray(groupData)) {
                actionModes.value = groupData;
                actionModesDisabled.value = false;
                actionDisabled.value = true;
            }

            // NO MODE
            else {
                actions.value = groupData;
                actionModesDisabled.value = true;

                // IF ONLY ONE CHOICE, SELECT THE ONLY CHOICE AND DISABLE IT
                if (actions.value.length == 1) {
                    selectedAction.value = actions.value.first().value;
                    actionDisabled.value = true;

                    if (value == 'specialization')
                        getSpecializationJobs();
                }
                else
                    actionDisabled.value = false;
            }
        }
    });

    watch(selectedActionMode, (value) => {
        selectedAction.value = '';

        if (selectedGroup.value != '' && value != '') {
            actions.value = manualExecutions[selectedGroup.value][value];
            actionDisabled.value = false;
        }
    });

    watch(selectedAction, (value) => {
        needApplication.value = false;
        needApplicationDomain.value = false;
        needProvider.value = false;
        selectedApplication.value = '';
        selectedApplicationDomain.value = '';
        selectedProvider.value = '';

        switch (value) {
            // APPLICATION NEEDED IN PARAMETERS
            case 'importfdv':
            case 'importcatalogue':
                needApplication.value = true;

                if (value == 'importcatalogue')
                    needApplicationDomain.value = true;

                break;

            case 'importdeliverylocationsegment mode=full':
                toast.warn(translate('warningSFAImportDeliveryLocationSegmentFull', 'toast'))
                break;
        }

        if (selectedGroup.value == 'edi') {
            // PROVIDER NEEDED IN PARAMETERS
            if (value != '')
                getProviders();

            needProvider.value = true;
        }
    });

    function jobTitle(job) {
        return `${job.subPath} - ${job.name}`;
    }

    function open() {
        selectedCustomer.value = '';
        selectedGroup.value = '';
        selectedActionMode.value = '';
        selectedAction.value = '';
        selectedApplication.value = '';
        selectedApplicationDomain.value = '';
        selectedProvider.value = '';
        selectedSpecializationJob.value = '';

        actionModes.value = {};
        actions.value = [];
        actionModesDisabled.value = true;
        actionDisabled.value = true;

        needApplication.value = false;
        needApplicationDomain.value = false;
        needProvider.value = false;

        visible.value = true;
    }

    function launch() {
        visible.value = false;

        let parameters = '';
        let actionGroup = '';
        let customer = selectedCustomer.value;
        let action = selectedAction.value;
        let provider = selectedProvider.value;
        let jobPath = (selectedSpecializationJob.value != '') ? specializationJobs.value.filter(sj => sj.name == selectedSpecializationJob.value).first().fullPath.replace(/\\/g, '\\\\') : null;

        switch (selectedGroup.value.toLowerCase()) {
            case 'dde':
                actionGroup = 'eCOS.DDE';
                parameters = 'cust=' + customer + ' action=' + action;

                if (selectedApplication.value != '') {
                    parameters += ' appIds=' + selectedApplication.value;
                    parameters += ' apps=' + applications.value.filter(a => a.id == parseInt(selectedApplication.value)).first().reference;
                }

                if (selectedApplicationDomain.value != '')
                    parameters += ' appDomainId=' + selectedApplicationDomain.value;

                break;

            case 'sfa':
                actionGroup = 'eCOS.SFA';
                parameters = 'cust=' + customer + ' action=' + action;
                break;

            case 'assortiment':
                actionGroup = 'eCOS.Assortiment';
                parameters = customer;
                break;

            case 'innodefinitive':
                actionGroup = 'eCOS.InnoDefinitive';
                parameters = 'cust=' + customer;
                break;

            case 'specialization':
                actionGroup = 'eCOS.Specialization';
                parameters = jobPath;
                break;

            case 'edi':
                actionGroup = 'eCOS.EDI.' + action;
                parameters = 'cust=' + customer + ' provider=' + provider;
                break;
        }

        let data = {
            action: 'ManualExecution',
            reference: customer,
            actionGroup: actionGroup,
            parameters: parameters,
        };

        $.getData(urlWebAPI + 'ProcessActivator', data)
            .then((logs) => {
                emit('execution-launched', data);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing ProcessActivator.');
            });
    }

    function getApplications() {
        let data = {
            token: userStore.getUser().token,
            customerReference: selectedCustomer.value,
        };

        $.getData(urlWebAPI + 'GetApplications', data)
            .then((data) => {
                applications.value = data;
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetApplications data.');
            });
    }

    function getProviders() {
        let data = {
            token: userStore.getUser().token,
            customerReference: selectedCustomer.value,
            providerTypes: Object.values(actions.value.filter(a => a.value.toLowerCase() == selectedAction.value.toLowerCase()).first().providers),
        };

        $.postData(urlWebAPI + 'GetProviders', data)
            .then((data) => {
                providers.value = data;
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetProviders data.');
            });
    }

    function getApplicationDomains() {
        const requestData = {
            token: userStore.getUser().token,
            customerReference: selectedCustomer.value,
        };

        $.getData(urlWebAPI + 'GetApplicationDomains', requestData)
            .then((response) => {
                applicationDomains.value = [allApplicationDomains, ...response];
            })
            .catch((error) => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetApplicationDomains data.');
            });
    }

    function getSpecializationJobs() {
        $.getData(urlWebAPI + 'GetSpecializationJobs', {})
            .then((data) => {
                specializationJobs.value = data.filter(d => d.customerReference == selectedCustomer.value);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetSpecializationJobs data.');
            });
    }

    defineExpose({ open });
</script>
